"use client";

import { useState } from "react";
import { CalendarDays, CheckCircle2, Circle, Clock, ListChecks, Mic, Send, Users, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DRAFT, EXAMPLE, NEXT_MEETING, PAST_OUTCOMES, WEEKDAYS, suggestDates, type GroupStage } from "./data";
import { PrimaryButton } from "./ui";

const STEPS: { stage: GroupStage; title: string; why: string }[] = [
    { stage: "plan", title: "Plan the year", why: "Pick your dates once; invitations and reminders then go out for you." },
    { stage: "before", title: "Meet", why: "At least four times a year. Meetings are recorded so you don't need a note-taker." },
    { stage: "after", title: "Confirm the minutes", why: "Draft minutes are ready for the Chair, who checks and confirms them within 72 hours." },
    { stage: "done", title: "Report shared", why: "Your report goes to the Governance Committee automatically." },
];

const ORDER: GroupStage[] = ["plan", "before", "after", "done"];

export function GroupView({ stage, asChair, onApproveYear, onConfirm }: {
    stage: GroupStage;
    asChair: boolean;
    onApproveYear: (count: number) => void;
    onConfirm: () => void;
}) {
    const now = ORDER.indexOf(stage);
    return (
        <div className="mx-auto max-w-3xl space-y-5">
            <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#007a8a]">Regular meetings</div>
                <h2 className="text-2xl font-bold text-[#003d45]">{EXAMPLE.name}</h2>
                <p className="mt-1 text-sm text-gray-500">Meeting regularly keeps the committee on track and lets the Governance Committee know how you&apos;re doing.</p>
            </div>

            {/* The four steps */}
            <ol id="gov-group-steps" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {STEPS.map((s, i) => {
                    const done = i < now || stage === "done";
                    const current = i === now && stage !== "done";
                    return (
                        <li key={s.stage} title={s.why}
                            className={`rounded-xl border p-3 ${current ? "border-[#007a8a] bg-[#007a8a]/5" : "border-gray-200 bg-white"}`}>
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-[#003d45]">
                                {done ? <CheckCircle2 className="size-4 text-emerald-600" /> : <Circle className={`size-4 ${current ? "text-[#007a8a]" : "text-gray-300"}`} />}
                                {s.title}
                            </div>
                            <p className="mt-1 text-xs leading-snug text-gray-500">{s.why}</p>
                        </li>
                    );
                })}
            </ol>

            <NowCard stage={stage} asChair={asChair} onApproveYear={onApproveYear} onConfirm={onConfirm} />

            {stage !== "plan" && <NextMeeting after={stage !== "before"} />}

            <Outcomes stage={stage} asChair={asChair} />
        </div>
    );
}

// ─── "What to do now" ─────────────────────────────────────────────────────────

function NowCard({ stage, asChair, onApproveYear, onConfirm }: { stage: GroupStage; asChair: boolean; onApproveYear: (n: number) => void; onConfirm: () => void }) {
    const shell = (title: string, when: string, why: string, body?: React.ReactNode, tone: "action" | "calm" = "action") => (
        <Card id="gov-group-now" className={tone === "action" ? "border-l-4 border-l-[#007a8a]" : ""}>
            <CardHeader>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">{tone === "action" ? "What to do now" : "Nothing to do right now"}</div>
                <CardTitle className="text-lg text-[#003d45]">{title}</CardTitle>
                <CardDescription className="flex flex-col gap-1">
                    <span className="flex items-center gap-1.5"><Clock className="size-3.5" />{when}</span>
                    <span><b className="text-gray-700">Why:</b> {why}</span>
                </CardDescription>
            </CardHeader>
            {body && <CardContent>{body}</CardContent>}
        </Card>
    );

    if (stage === "plan") {
        return asChair
            ? shell("Plan your 2027 meetings", "By 31 December", "Every committee meets at least four times a year. Choosing the dates once means invitations and reminders go out on their own.", <PlanForm onApprove={onApproveYear} />)
            : shell("Your Chair is planning 2027", "Invitations will arrive by 31 December", "Fabien is choosing next year's dates. You'll get each meeting in your calendar.", undefined, "calm");
    }
    if (stage === "before") {
        return asChair
            ? shell("Just join on Thursday", NEXT_MEETING.date + ", " + NEXT_MEETING.time, "Invitations and reminders have gone out for you. The meeting is recorded, so draft minutes will be ready for you afterwards.", undefined, "calm")
            : shell("Join on Thursday", NEXT_MEETING.date + ", " + NEXT_MEETING.time, "Have a look at what's being discussed below, so you can come ready.", undefined, "calm");
    }
    if (stage === "after") {
        return asChair
            ? shell("Confirm the minutes from Thursday", "Within 72 hours of the meeting — 61 hours left", "So everyone knows what was decided and who's doing what, and so the Governance Committee gets your report.", <ConfirmMinutes onConfirm={onConfirm} />)
            : shell("Fabien is checking the minutes", "Usually within 3 days", "You'll see the decisions and any tasks for you here once they're confirmed.", undefined, "calm");
    }
    return asChair
        ? shell("All done until the next meeting", "Next meeting: Thu 14 January 2027", "The minutes are confirmed, saved to the committee's Teams channel, and your report has gone to the Governance Committee.", undefined, "calm")
        : shell("You have a new task", "Due 27 November", "It was agreed at Thursday's meeting. Accept it so everyone knows it's in hand.", <TaskAccept />);
}

function PlanForm({ onApprove }: { onApprove: (n: number) => void }) {
    const [weekday, setWeekday] = useState(4);
    const [time, setTime] = useState("12:00");
    const [count, setCount] = useState(6);
    const dates = suggestDates(2027, weekday, count);
    return (
        <div id="gov-group-plan" className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm font-medium text-gray-700">Day
                    <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm font-normal">
                        {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
                    </select>
                </label>
                <label className="text-sm font-medium text-gray-700">Time
                    <input type="time" value={time} onChange={(e) => setTime(e.target.value || "12:00")} className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm font-normal" />
                </label>
                <div className="text-sm font-medium text-gray-700">How many
                    <div className="mt-1 flex gap-1">
                        {[4, 6, 8].map((n) => (
                            <button key={n} type="button" onClick={() => setCount(n)}
                                className={`flex-1 rounded-lg border py-1.5 text-sm ${count === n ? "border-[#007a8a] bg-[#007a8a] text-white" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>{n}</button>
                        ))}
                    </div>
                    <div className="mt-1 text-xs font-normal text-gray-400">6 is recommended</div>
                </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {dates.map((d) => <span key={d} className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">{d}</span>)}
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton size="lg" onClick={() => onApprove(count)}><Send />Approve and send invitations</PrimaryButton>
                <span className="text-xs text-gray-500">All {EXAMPLE.members.length} members are invited in Teams. Reminders go a week and a day before.</span>
            </div>
        </div>
    );
}

function ConfirmMinutes({ onConfirm }: { onConfirm: () => void }) {
    const [answer, setAnswer] = useState("");
    const [full, setFull] = useState(false);
    return (
        <div id="gov-group-confirm" className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-gray-50 p-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500"><Users className="size-3.5" />Attended</div>
                    <div className="mt-1 text-sm font-semibold text-gray-800">{DRAFT.attended} of {EXAMPLE.members.length} · enough to decide</div>
                    <div className="mt-0.5 text-xs text-gray-500">{DRAFT.names.join(", ")}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 sm:col-span-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500"><CheckCircle2 className="size-3.5" />Decided</div>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-gray-800">{DRAFT.decisions.map((x) => <li key={x}>{x}</li>)}</ul>
                </div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500"><ListChecks className="size-3.5" />Who&apos;s doing what</div>
                <ul className="mt-1 space-y-1 text-sm">
                    {DRAFT.tasks.map((t) => <li key={t.text} className="flex flex-wrap justify-between gap-2"><span className="text-gray-800">{t.text}</span><span className="text-gray-500">{t.owner} · by {t.due}</span></li>)}
                </ul>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="text-sm font-semibold text-amber-900">One thing to check: {DRAFT.check.question}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {DRAFT.check.options.map((o) => (
                        <button key={o} type="button" onClick={() => setAnswer(o)}
                            className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${answer === o ? "border-[#007a8a] bg-[#007a8a] text-white" : "border-amber-300 bg-white text-gray-700"}`}>{o}</button>
                    ))}
                </div>
            </div>
            {full && (
                <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 p-3 font-sans text-xs text-gray-700">
{`Curriculum & Testing Committee — Thursday 12 November, 12:00 pm ET
Present: ${DRAFT.names.join(", ")}. Apologies: Dave Tonaszuck, Shawn Keel.

1. December exams — results to be published within six weeks of the exam; Keith to draft the candidate notice.
2. Level III item writers — Trent has four volunteers so far; two more needed by the end of November.
3. Annual review of the Exam Deferral, Cancellation, Retake, and Refund Policy — no changes this year; Fabien will send the review to the Governance Committee.
4. 2027 dates — Thursday evenings suit everyone.`}
                </pre>
            )}
            <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton size="lg" disabled={!answer} onClick={onConfirm}><CheckCircle2 />Confirm the minutes</PrimaryButton>
                <Button variant="ghost" onClick={() => setFull(!full)}>{full ? "Hide" : "Read"} the full draft</Button>
            </div>
            <p className="text-xs text-gray-500">Confirming saves the minutes to the committee&apos;s Teams channel, sends each task to its owner, and shares your report with the Governance Committee. If you&apos;re away, Keith (Vice Chair) can confirm.</p>
        </div>
    );
}

function TaskAccept() {
    const [state, setState] = useState<"open" | "accepted" | "declined">("open");
    return (
        <div id="gov-group-task" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 p-3">
            <div>
                <div className="text-sm font-semibold text-gray-800">Confirm six Level III item writers</div>
                <div className="text-xs text-gray-500">From the 12 November meeting</div>
            </div>
            {state === "open" ? (
                <div className="flex gap-2">
                    <PrimaryButton onClick={() => setState("accepted")}>Accept</PrimaryButton>
                    <Button variant="outline" onClick={() => setState("declined")}>Decline</Button>
                </div>
            ) : <span className={`text-sm font-semibold ${state === "accepted" ? "text-emerald-600" : "text-gray-500"}`}>{state === "accepted" ? "✓ Accepted" : "Declined — Fabien has been told"}</span>}
        </div>
    );
}

// ─── Next meeting & outcomes ─────────────────────────────────────────────────

function NextMeeting({ after }: { after: boolean }) {
    if (after) {
        return (
            <Card size="sm">
                <CardContent className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-2 text-gray-700"><CalendarDays className="size-4 text-[#007a8a]" />Next meeting: <b>Thu 14 January 2027</b>, 12:00 pm ET</span>
                    <span className="text-xs text-gray-400">Agenda will appear a week before</span>
                </CardContent>
            </Card>
        );
    }
    return (
        <Card id="gov-group-next">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#003d45]"><CalendarDays className="size-4 text-[#007a8a]" />Next meeting · {NEXT_MEETING.date}</CardTitle>
                <CardDescription>{NEXT_MEETING.time} · Microsoft Teams · reminders sent 5 and 11 November</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">What we&apos;ll discuss</div>
                    <ol className="space-y-2">
                        {NEXT_MEETING.agenda.map((a, i) => (
                            <li key={a.item} className="flex gap-3">
                                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#003d45] text-xs font-semibold text-white">{i + 1}</span>
                                <div><div className="text-sm font-medium text-gray-800">{a.item}</div><div className="text-xs text-gray-500">{a.why}</div></div>
                            </li>
                        ))}
                    </ol>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
                    <span className="flex items-center gap-1.5 text-xs text-gray-500"><Mic className="size-3.5" />Recorded so minutes can be drafted. The recording stays within the committee.</span>
                    <PrimaryButton><Video />Join in Teams</PrimaryButton>
                </div>
            </CardContent>
        </Card>
    );
}

function Outcomes({ stage, asChair }: { stage: GroupStage; asChair: boolean }) {
    const latest = stage === "done" ? [{
        date: "Thu 12 November", attended: `${DRAFT.attended} of ${EXAMPLE.members.length}`, decisions: DRAFT.decisions,
        tasks: DRAFT.tasks.map((t) => ({ text: t.text, owner: t.owner, done: false })),
    }] : [];
    return (
        <Card id="gov-group-outcomes">
            <CardHeader>
                <CardTitle className="text-[#003d45]">What was decided</CardTitle>
                <CardDescription>Outcomes and tasks from past meetings{asChair ? "" : " — the minutes are in the committee's Teams channel"}.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {[...latest, ...PAST_OUTCOMES].map((m, i) => (
                    <div key={m.date} className={i > 0 ? "border-t border-gray-100 pt-4" : ""}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-800">{m.date}</span>
                            <span className="text-xs text-gray-400">{m.attended} attended</span>
                        </div>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-gray-700">{m.decisions.map((x) => <li key={x}>{x}</li>)}</ul>
                        <div className="mt-2 space-y-1">
                            {m.tasks.map((t) => (
                                <div key={t.text} className="flex items-center gap-2 text-xs text-gray-500">
                                    {t.done ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Circle className="size-3.5 text-gray-300" />}
                                    {t.text} — {t.owner}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
